import type { SimulationInput, SimulationResults } from './api/types'

// Outstanding principal and cumulative interest paid after k monthly
// payments of a fixed-rate amortizing loan of n monthly payments.
function loanAmortization(principal: number, monthlyRate: number, n: number, k: number) {
  if (n <= 0 || principal <= 0) return { remaining: 0, interestPaid: 0 }
  const kc = Math.min(k, n)
  if (kc <= 0) return { remaining: principal, interestPaid: 0 }
  let payment: number
  let remaining: number
  if (monthlyRate > 0) {
    payment = (principal * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -n))
    remaining =
      (principal * (Math.pow(1 + monthlyRate, n) - Math.pow(1 + monthlyRate, kc))) /
      (Math.pow(1 + monthlyRate, n) - 1)
  } else {
    payment = principal / n
    remaining = principal - payment * kc
  }
  return { remaining, interestPaid: Math.max(0, payment * kc - (principal - remaining)) }
}

// Early-repayment penalty (IRA) capped at the legal maximum (Code de la
// consommation, art. R313-25): the lower of 6 months of interest on the
// reimbursed capital, and 3% of that capital.
function earlyRepaymentIRA(remainingCapital: number, monthlyRate: number) {
  return Math.min(remainingCapital * monthlyRate * 6, remainingCapital * 0.03)
}

// Suggested default for `resaleFees`: the IRA plus the loan interest paid
// over the planned holding period.
export function computeResaleFeesDefault(s: SimulationInput): number {
  const n = s.loanDurationYears * 12
  const monthlyRate = s.loanRate / 100 / 12
  const { remaining, interestPaid } = loanAmortization(
    s.loanPrincipal,
    monthlyRate,
    n,
    s.holdingMonths,
  )
  return earlyRepaymentIRA(remaining, monthlyRate) + interestPaid
}

// IS bracket: 15% on the margin up to the threshold, 25% above it.
const CAPITAL_GAINS_TAX_THRESHOLD = 42500

function computeCapitalGainsTax(netMarginBeforeTax: number): number {
  const base = Math.max(0, netMarginBeforeTax)
  if (base <= CAPITAL_GAINS_TAX_THRESHOLD) return base * 0.15
  return CAPITAL_GAINS_TAX_THRESHOLD * 0.15 + (base - CAPITAL_GAINS_TAX_THRESHOLD) * 0.25
}

// Client-side mirror of the backend Simulation.Results() computation,
// used for live preview in the form. The backend remains the source of truth.
export function computeResults(s: SimulationInput): SimulationResults {
  const notaryFeeAmount = (s.purchasePrice * s.notaryFeeRate) / 100
  const pricePerM2 = s.surface > 0 ? s.purchasePrice / s.surface : 0
  const investmentCost = s.purchasePrice + notaryFeeAmount + s.renovationCosts + s.compromis
  const resaleEstimate = s.surface * s.pricePerM2Resale

  const n = s.loanDurationYears * 12
  const monthlyRate = s.loanRate / 100 / 12
  let monthlyLoanPayment = 0
  if (n > 0) {
    monthlyLoanPayment =
      monthlyRate > 0
        ? (s.loanPrincipal * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -n))
        : s.loanPrincipal / n
  }
  const totalInterest = Math.max(0, monthlyLoanPayment * n - s.loanPrincipal)

  const { remaining: remainingLoanCapital, interestPaid: interestDuringHolding } = loanAmortization(
    s.loanPrincipal,
    monthlyRate,
    n,
    s.holdingMonths,
  )
  const earlyRepaymentIra = earlyRepaymentIRA(remainingLoanCapital, monthlyRate)
  const resaleFeesDefault = earlyRepaymentIra + interestDuringHolding

  const annualRent = s.monthlyRent * 12
  const annualOperatingExpenses =
    (s.monthlyPropertyTax + s.monthlyCondoFees + s.monthlyInsurancePno + s.monthlyManagementFee) *
    12
  const annualDebtService = monthlyLoanPayment * 12
  const annualCashFlow = annualRent - annualOperatingExpenses - annualDebtService

  const grossMargin = resaleEstimate - investmentCost
  const grossMarginPercent = investmentCost > 0 ? (grossMargin / investmentCost) * 100 : 0
  const netMarginChargePercent =
    investmentCost > 0
      ? ((resaleEstimate - investmentCost - s.resaleFees) / investmentCost) * 100
      : 0
  const netMarginBeforeTax = grossMargin - s.resaleFees
  const capitalGainsTaxAmount = computeCapitalGainsTax(netMarginBeforeTax)
  const netMargin = netMarginBeforeTax - capitalGainsTaxAmount
  const roi = investmentCost > 0 ? (netMargin / investmentCost) * 100 : 0
  const annualizedReturn = s.holdingMonths > 0 ? (roi / s.holdingMonths) * 12 : 0

  return {
    notaryFeeAmount,
    pricePerM2,
    investmentCost,
    resaleEstimate,
    monthlyLoanPayment,
    totalInterest,
    annualRent,
    annualOperatingExpenses,
    annualDebtService,
    monthlyCashFlow: annualCashFlow / 12,
    annualCashFlow,
    grossYield: investmentCost > 0 ? (annualRent / investmentCost) * 100 : 0,
    netYield:
      investmentCost > 0 ? ((annualRent - annualOperatingExpenses) / investmentCost) * 100 : 0,
    financialYield: s.downPayment > 0 ? (annualCashFlow / s.downPayment) * 100 : 0,
    grossMargin,
    grossMarginPercent,
    netMarginChargePercent,
    capitalGainsTaxAmount,
    netMarginBeforeTax,
    netMargin,
    roi,
    annualizedReturn,
    remainingLoanCapital,
    interestDuringHolding,
    earlyRepaymentIra,
    resaleFeesDefault,
  }
}
